import { useState, useCallback, useRef, useEffect } from "react";
import {
    getTableDataFromApi,
    buildBaseApiParams,
    buildActionApiParams,
    formatCSVFileName,
    buildDateCategory as buildDateCategoryUtil,
} from "./drillDownHelpers";
import { getFleetPerformanceAnalyticsListActions } from "../../../../Api/FleetPerformanceApi";
import { usePagesContent } from "../../../../Hooks/usePagesContent";
import {
    buildFleetMetaInfoResolver,
    resolveFleetTableConfig,
} from "../../fleetPerformanceTableConfig";

/**
 * Normalizes table data response to ensure a consistent contract
 */
const normalizeTableResponse = (res) => {
    if (!res) return { data: [], total: 0 };
    return {
        data: res.data || [],
        total: res.total !== undefined ? res.total : (res.totalCount !== undefined ? res.totalCount : (res.data?.length || 0))
    };
};

/**
 * Custom hook for managing fleet chart drill-down functionality
 */
export const useFleetChartDrillDown = ({
    startDate,
    endDate,
    filters = [],
    globalFilters = {},
    config = {},
}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDataPoint, setSelectedDataPoint] = useState(null);

    const pagesContent = usePagesContent();
    const orgMetaTables = pagesContent?.metaInfo?.FleetPerformance?.table;

    const isMounted = useRef(true);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const buildDateCategory = useCallback(
        (dataPoint) => {
            if (config.buildDateCategory) {
                return config.buildDateCategory(dataPoint);
            }
            return buildDateCategoryUtil(dataPoint, startDate, endDate);
        },
        [config.buildDateCategory, startDate, endDate]
    );

    const getTableData = useCallback(
        async (selectedDataPointParam, pageNumber, rowsPerPage, sortBy) => {
            if (config.getTableData) {
                return config.getTableData(selectedDataPointParam, pageNumber, rowsPerPage, sortBy, globalFilters, startDate, endDate);
            }

            const dataPoint = selectedDataPointParam || selectedDataPoint;
            if (!dataPoint || !startDate || !endDate) {
                return { data: [], total: 0 };
            }

            const groupBy = config.getLevel1GroupBy ? config.getLevel1GroupBy(dataPoint) : ["originCity"];
            const select = config.getLevel1Select ? config.getLevel1Select() : ["DistinctVehicleCount"];
            const metaInfo = config.getLevel1MetaInfo ? config.getLevel1MetaInfo() : ["licensePlateNumber"];
            const apiFilters = [...filters];
            const filtersWithDefaults = config.getLevel1Filters ? config.getLevel1Filters(dataPoint, apiFilters) : apiFilters;

            const baseApiParams = buildBaseApiParams(
                dataPoint,
                startDate,
                endDate,
                groupBy,
                select,
                metaInfo,
                filtersWithDefaults,
                { pageNum: pageNumber, pageCount: rowsPerPage }
            );

            if (sortBy && sortBy.length > 0) {
                baseApiParams.sortBy = JSON.stringify([...sortBy]);
            }

            const res = await getTableDataFromApi(globalFilters, baseApiParams);
            if (!isMounted.current) return { data: [], total: 0 };
            return normalizeTableResponse(res);
        },
        [config, selectedDataPoint, startDate, endDate, filters, globalFilters]
    );

    // Level 1: Apply table action (download / email)
    const applyTableAction = useCallback(
        async (rowSelectionKeys, actionType, selectAll, params = {}) => {
            if (config.applyTableAction) {
                return config.applyTableAction(rowSelectionKeys, actionType, selectAll, params, selectedDataPoint, globalFilters, startDate, endDate);
            }

            if (!selectedDataPoint || !startDate || !endDate) {
                return actionType === "download" ? [] : null;
            }

            const groupBy = config.getLevel1GroupBy ? config.getLevel1GroupBy(selectedDataPoint) : ["originCity"];
            const select = config.getLevel1Select ? config.getLevel1Select() : ["DistinctVehicleCount"];
            const metaInfo = config.getLevel1MetaInfo ? config.getLevel1MetaInfo() : ["licensePlateNumber"];
            const apiFilters = [...filters];
            const filtersWithDefaults = config.getLevel1Filters
                ? config.getLevel1Filters(selectedDataPoint, apiFilters)
                : apiFilters;

            const baseApiParams = buildBaseApiParams(
                selectedDataPoint,
                startDate,
                endDate,
                groupBy,
                select,
                metaInfo,
                filtersWithDefaults
            );

            const apiParams = buildActionApiParams(
                baseApiParams,
                actionType,
                rowSelectionKeys,
                selectAll,
                params
            );

            const res = await getFleetPerformanceAnalyticsListActions(globalFilters, apiParams);
            if (!isMounted.current) return actionType === "download" ? [] : null;

            if (res && res.responseStatus?.code === 200) {
                return res.response?.data || (actionType === "download" ? [] : null);
            }
            return actionType === "download" ? [] : null;
        },
        [config, selectedDataPoint, startDate, endDate, filters, globalFilters]
    );

    // Level 1: Get CSV filename
    const getCSVFileName = useCallback(() => {
        if (config.getCSVFileName) {
            const result = config.getCSVFileName(selectedDataPoint);
            if (result) return result;
        }

        const prefix = config.csvFileNamePrefix || "FleetPerformance_Chart";
        const metricName = selectedDataPoint?.seriesName || selectedDataPoint?.projectName || "Fleet";
        const parts = [metricName];

        return formatCSVFileName(
            prefix,
            parts,
            selectedDataPoint?.startDate || startDate,
            selectedDataPoint?.endDate || endDate
        );
    }, [config, selectedDataPoint, startDate, endDate]);

    const getTableConfig = useCallback(
        (level = 1, dataPoint = null) => {
            if (!config.getTableConfig) return null;
            const fallback = config.getTableConfig(level, dataPoint || selectedDataPoint);
            return resolveFleetTableConfig(orgMetaTables, config.orgMetaTableKeys?.[level], fallback);
        },
        [config.getTableConfig, config.orgMetaTableKeys, orgMetaTables, selectedDataPoint]
    );

    // Passed to the chart's own level 2 fetchers so a configured column is
    // actually requested. Each keeps its own base list and wraps it with this.
    const resolveLevel2MetaInfo = useCallback(
        (baseFields) =>
            buildFleetMetaInfoResolver(
                resolveFleetTableConfig(orgMetaTables, config.orgMetaTableKeys?.[2], null)
                    ?.tableColumnsConfig
            )(baseFields),
        [orgMetaTables, config.orgMetaTableKeys]
    );

    const level2GetTableData = useCallback(
        async (level2Record, pageNumber, rowsPerPage, sortBy, selectedCustomFilters) => {
            if (config.level2GetTableData) {
                return config.level2GetTableData(level2Record, selectedDataPoint, pageNumber, rowsPerPage, sortBy, globalFilters, selectedCustomFilters, { resolveMetaInfo: resolveLevel2MetaInfo });
            }

            if (!selectedDataPoint || !startDate || !endDate || !level2Record) {
                return { data: [], total: 0 };
            }

            const groupBy = config.getLevel2GroupBy ? config.getLevel2GroupBy(level2Record) : ["licensePlateNumber"];
            const select = config.getLevel2Select ? config.getLevel2Select() : ["DistinctVehicleCount"];
            const metaInfo = resolveLevel2MetaInfo(config.getLevel2MetaInfo ? config.getLevel2MetaInfo() : []);
            const apiFilters = [...filters];
            const filtersWithDefaults = config.getLevel2Filters ? config.getLevel2Filters(level2Record, selectedDataPoint, apiFilters) : apiFilters;

            const baseApiParams = buildBaseApiParams(
                selectedDataPoint,
                startDate,
                endDate,
                groupBy,
                select,
                metaInfo,
                filtersWithDefaults,
                { pageNum: pageNumber, pageCount: rowsPerPage }
            );

            if (sortBy && sortBy.length > 0) {
                baseApiParams.sortBy = JSON.stringify([...sortBy]);
            }

            const res = await getTableDataFromApi(globalFilters, baseApiParams);
            if (!isMounted.current) return { data: [], total: 0 };
            return normalizeTableResponse(res);
        },
        [config, selectedDataPoint, startDate, endDate, filters, globalFilters, resolveLevel2MetaInfo]
    );

    // Level 2: Apply table action (download / email)
    const level2ApplyTableAction = useCallback(
        async (rowSelectionKeys, actionType, selectAll, params = {}, level2Record) => {
            if (config.level2ApplyTableAction) {
                return config.level2ApplyTableAction(
                    rowSelectionKeys,
                    actionType,
                    selectAll,
                    params,
                    level2Record,
                    selectedDataPoint,
                    globalFilters,
                    startDate,
                    endDate,
                    { resolveMetaInfo: resolveLevel2MetaInfo }
                );
            }

            if (!selectedDataPoint || !startDate || !endDate || !level2Record) {
                return actionType === "download" ? [] : null;
            }

            const groupBy = config.getLevel2GroupBy ? config.getLevel2GroupBy(level2Record) : ["licensePlateNumber"];
            const select = config.getLevel2Select ? config.getLevel2Select() : ["DistinctVehicleCount"];
            const metaInfo = resolveLevel2MetaInfo(config.getLevel2MetaInfo ? config.getLevel2MetaInfo() : []);
            const apiFilters = [...filters];
            const filtersWithDefaults = config.getLevel2Filters
                ? config.getLevel2Filters(level2Record, selectedDataPoint, apiFilters)
                : apiFilters;

            const baseApiParams = buildBaseApiParams(
                selectedDataPoint,
                startDate,
                endDate,
                groupBy,
                select,
                metaInfo,
                filtersWithDefaults
            );

            const apiParams = buildActionApiParams(
                baseApiParams,
                actionType,
                rowSelectionKeys,
                selectAll,
                params
            );

            const res = await getFleetPerformanceAnalyticsListActions(globalFilters, apiParams);
            if (!isMounted.current) return actionType === "download" ? [] : null;

            if (res && res.responseStatus?.code === 200) {
                return res.response?.data || (actionType === "download" ? [] : null);
            }
            return actionType === "download" ? [] : null;
        },
        [config, selectedDataPoint, startDate, endDate, filters, globalFilters, resolveLevel2MetaInfo]
    );

    // Level 2: Get CSV filename
    const level2GetCSVFileName = useCallback(
        (level2Record) => {
            if (config.level2GetCSVFileName) {
                const result = config.level2GetCSVFileName(level2Record, selectedDataPoint);
                if (result) return result;
            }

            const prefix = config.csvFileNamePrefix || "FleetPerformance_Vehicles";
            const groupLabel = level2Record?.xLabel || level2Record?.label || "Group";
            const metricName = selectedDataPoint?.seriesName || selectedDataPoint?.projectName || "Fleet";
            const parts = [groupLabel, metricName];

            return formatCSVFileName(
                prefix,
                parts,
                selectedDataPoint?.startDate || startDate,
                selectedDataPoint?.endDate || endDate
            );
        },
        [config, selectedDataPoint, startDate, endDate]
    );

    const handleDataPointClick = useCallback((clickedData) => {
        setSelectedDataPoint(clickedData);
        setModalOpen(true);
    }, []);

    const handleModalClose = useCallback(() => {
        setModalOpen(false);
        setSelectedDataPoint(null);
    }, []);

    const getModalTitle = useCallback(
        (dataPoint) => {
            if (config.getModalTitle) {
                return config.getModalTitle(dataPoint || selectedDataPoint, startDate, endDate);
            }
            return buildDateCategory(dataPoint || selectedDataPoint);
        },
        [config.getModalTitle, selectedDataPoint, buildDateCategory]
    );

    const level2Title = useCallback(
        (level2Record) => {
            if (config.level2Title) {
                return config.level2Title(level2Record);
            }
            return level2Record?.xLabel || level2Record?.label || "Details";
        },
        [config.level2Title]
    );

    return {
        modalOpen,
        selectedDataPoint,
        handleDataPointClick,
        handleModalClose,
        getTableData,
        applyTableAction,
        getCSVFileName,
        getTableConfig,
        level2GetTableData,
        level2ApplyTableAction,
        level2GetCSVFileName,
        buildDateCategory,
        getModalTitle,
        level2Title
    };
};